<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    fullscreen
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }" @click.stop data-cy="list-podNodeItem-actions-sharedFilesList-activator" />
    </template>

    <v-card data-cy="dialog-sharedFilesList-modal">
      <v-card-title
        class="d-flex justify-space-between"
        data-cy="dialog-sharedFilesList-title"
      >
        {{ $t('routes.node.sharedFileLinks') }}

        <v-btn icon @click.stop="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="mt-4">
        <v-data-iterator
          :items="getFilteredAccesslinks"
          :items-per-page="itemsPerPage"
          :custom-sort="customSort"
          :loading="isLoading"
          style="width: 100%;"
        >
          <template v-slot:loader>
            <v-col class="text-center">
              <v-progress-circular
                indeterminate
                size="70"
                width="7"
                color="primary"
                :aria-label="$t('routes.node.loadingSharedFilesLinksList')"
              />
              <div>{{ $t('routes.node.loadingSharedFilesLinksList') }}</div>
            </v-col>
          </template>

          <template v-slot:default="props">
            <div>
              <v-expansion-panels variant="accordion" class="custom-panels">
                <v-expansion-panel v-for="{ raw: item } in props.items" :key="item.code" class="bg-list">
                  <v-expansion-panel-title class="py-4" aria-hidden="true">
                    <v-row>
                      <v-col cols="12" sm="6" md="7">
                        <div class="d-flex flex-column">
                          <div v-if="item.name" class="text-subtitle-2 font-weight-bold mb-1">
                            {{ item.name }}
                          </div>

                          <!-- Path display -->
                          <div class="path-section">
                            <template
                              v-if="item.pods.length > 0 && item.pods[0].nodeid && getNodeById(item.pods[0].nodeid)">
                              <router-link :to="`/node/${getNodeById(item.pods[0].nodeid).id}`">
                                {{ getNodeById(item.pods[0].nodeid).name }}
                              </router-link>
                              <span class="mx-1">/</span>
                            </template>
                            <template
                              v-else-if="item.nodes.length > 0 && item.nodes[0].parentid && getNodeById(item.nodes[0].parentid)">
                              <router-link :to="`/node/${item.nodes[0].parentid}`">
                                {{ getNodeById(item.nodes[0].parentid).name }}
                              </router-link>
                              <span class="mx-1">/</span>
                            </template>

                            <router-link v-if="item.pods.length > 0"
                                         :to="`/pod/${item.pods[0].id}`">
                              {{ item.pods[0].name }}
                            </router-link>
                            <router-link v-else-if="item.nodes.length > 0"
                                         :to="`/node/${item.nodes[0].id}`">
                              {{ item.nodes[0].name }}
                            </router-link>

                            <v-icon v-if="item.teamonly" color="primary" size="small" class="ml-2"
                                    :title="$t('common.teamonly')"
                            >
                              mdi-account-lock-outline
                            </v-icon>
                          </div>

                          <!-- Additional files/folders count -->
                          <div v-if="(item.pods.length + item.nodes.length) >= 2">
                            {{ getOtherFilesFoldersTranslationKey(item.pods.length, item.nodes.length) }}
                          </div>

                          <!-- Enabled options -->
                          <div v-if="enabledOptionsLabels(item).length > 0" class="mt-2">
                            <v-chip
                              v-for="(label, idx) in enabledOptionsLabels(item)"
                              :key="idx"
                              class="mr-2 my-1"
                              size="small"
                              variant="outlined"
                              :text="label"
                            />
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12" sm="3" md="2">
                        <div class="date-info" :class="{'text-error': isExpired(item)}">
                          {{ $t('routes.node.expiresAt') }}:
                          <div class="font-weight-medium">
                            {{ isoToDatetime(utc(item.expireAt)) }}
                          </div>
                        </div>

                        <AccesslinkUpdateDialog
                          v-if="canControlAccesslinks"
                          :accesslink="item"
                          :rootNode="node"
                        >
                          <v-btn elevation="0" class="mt-2 px-4" variant="text" height="32px">
                            <v-icon start size="small" color="primary">mdi-pencil</v-icon>
                            <span class="text-primary">{{ $t('common.edit') }}</span>
                          </v-btn>
                        </AccesslinkUpdateDialog>
                      </v-col>

                      <v-col cols="12" sm="3" md="3">
                        <div class="date-info">
                          {{ $t('routes.node.lastUpdatedAt') }}:
                          <div class="font-weight-medium">{{ isoToDatetime(item.updatedAt) }}</div>
                        </div>

                        <AccesslinkDeleteDialog
                          v-if="canControlAccesslinks"
                          :accesslink="item"
                        >
                          <template v-slot:activator="{ props }">
                            <v-btn v-bind="props"
                                   class="mt-2 px-4"
                                   elevation="0"
                                   height="32px"
                                   variant="text"
                                   color="error"
                            >
                              <v-icon start size="small" color="error">mdi-delete</v-icon>
                              <span class="text-error">{{ $t('common.delete') }}</span>
                            </v-btn>
                          </template>
                        </AccesslinkDeleteDialog>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <v-card flat class="pa-3">
                      <v-row>
                        <v-col col="12">
                          <!-- URL Section -->
                          <div class="url-section mb-4">
                            <div class="d-flex flex-row align-end">
                              <div class="flex-grow-1">
                                <div class="text-subtitle-2 mb-1">
                                  {{ $t('routes.node.URL') }}
                                </div>
                                <div class="url-display pa-2 rounded-lg d-flex"
                                    :class="$vuetify.theme.name === 'dark' ? 'dark' : 'light'"
                                    @dblclick="selectAndCopyURLToClipboard($event, item)"
                                >
                                  <div class="flex-grow-1 d-flex align-center">
                                    {{ getItemUrl(item) }}
                                  </div>
                                  <div class="flex-grow-0">
                                    <v-btn
                                      v-if="isCopyToClipboardSupported"
                                      :aria-label="$t('routes.pod.menuAccesslink.copyToClipboard')"
                                      size="small"
                                      density="comfortable"
                                      variant="text"
                                      width="36"
                                      height="36"
                                      rounded="36"
                                      icon tile
                                      @click="copyURLToClipboard(item)"
                                    >
                                      <v-icon>mdi-content-copy</v-icon>
                                    </v-btn>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Password Section -->
                          <div v-if="item.password" class="password-section mb-4">
                            <div class="d-flex flex-row align-end">
                              <div class="flex-grow-1">
                                <div class="text-subtitle-2 mb-1">
                                  {{ $t('routes.node.password') }}
                                </div>
                                <div
                                  class="password-display pa-2 rounded-lg d-flex"
                                  :class="$vuetify.theme.name === 'dark' ? 'dark' : 'light'"
                                  @dblclick="selectAndCopyPasswordToClipboard($event, item)"
                                >
                                  <div class="flex-grow-1 d-flex align-center">
                                    {{ item.password }}
                                  </div>
                                  <div class="flex-grow-0">
                                    <v-btn
                                      v-if="isCopyToClipboardSupported"
                                      size="small"
                                      density="comfortable"
                                      variant="text"
                                      width="32"
                                      height="32"
                                      rounded="lg"
                                      icon tile
                                      @click="copyPasswordToClipboard(item)"
                                    >
                                      <v-icon>mdi-content-copy</v-icon>
                                    </v-btn>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </div>

                          <!-- Notes Section -->
                          <div v-if="getNotes(item)" class="notes-section mb-4">
                            <div class="text-subtitle-2 mb-1">Notes</div>
                            <div v-html="getNotes(item)" class="markdown-preview pa-3 rounded-lg"
                                :class="$vuetify.theme.name === 'dark' ? 'dark' : 'light'"></div>
                          </div>

                          <!-- Files Section -->
                          <div v-if="item.pods.length > 0" class="files-section mb-4">
                            <div class="text-subtitle-2 mb-2">{{ $t('routes.node.files') }}</div>
                            <div v-for="pod in orderPodsByName(item.pods)" :key="pod.id">
                              <span v-if="pod.nodeid && getNodeById(pod.nodeid)">
                                <router-link :to="`/node/${getNodeById(pod.nodeid).id}`">
                                  {{ getNodeById(pod.nodeid).name }}
                                </router-link> /
                              </span>
                              <router-link :to="`/pod/${pod.id}`">
                                {{ pod.name }}
                              </router-link>
                            </div>
                          </div>

                          <!-- Folders Section -->
                          <div v-if="item.nodes.length > 0" class="nodes-section mb-4">
                            <div class="text-subtitle-2 mb-2">{{ $t('routes.node.nodes') }}</div>
                            <div v-for="node in orderNodesByName(item.nodes)" :key="node.id">
                              <span v-if="node.parentid && getNodeById(node.parentid)">
                                <router-link :to="`/node/${node.parentid}`">
                                  {{ getNodeById(node.parentid).name }}
                                </router-link> /
                              </span>
                              <router-link :to="`/node/${node.id}`">
                                {{ node.name }}
                              </router-link>
                            </div>
                          </div>

                          <!-- Invitations Section -->
                          <div v-if="item?.invitations?.length > 0" class="invitations-section mb-4">
                            <div class="text-subtitle-2 mb-2">
                              {{ $t('routes.pod.menuAccesslink.invitedPeople') }}
                            </div>
                            <div class="d-flex flex-wrap">
                              <v-chip
                                v-for="invitation in item.invitations"
                                :key="invitation.id"
                                size="small"
                                class="mr-2 mb-2"
                                color="text-primary"
                                variant="outlined"
                              >
                                <v-icon start size="small">mdi-email-outline</v-icon>
                                {{ invitation.email }}
                              </v-chip>
                            </div>
                          </div>
                        </v-col>
                      </v-row>


                    </v-card>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

            </div>
          </template>

          <template v-slot:footer="{ page, items, pageCount, prevPage, nextPage }">
            <div v-if="items.length > 0" class="d-flex align-center justify-end mx-1 mt-4 ml-auto">
              <span id="accesslink-list-dialog-items-per-page" class="mr-8">
                {{ $t('common.itemsPerPage') }}
              </span>
              <v-select
                v-model="itemsPerPage"
                aria-labelledby="accesslink-list-dialog-items-per-page"
                density="compact"
                style="max-width: 70px"
                class="mr-8"
                variant="underlined"
                :items="[10, 20, 30, 50]"
                eager
                hide-details
              />
              <span class="mr-8">
                {{
                  $t('common.fromToOutOf', {
                    from: Math.max(1, (page - 1) * itemsPerPage),
                    to: Math.min(items.length, page * itemsPerPage),
                    total: items.length
                  })
                }}
              </span>
              <v-btn
                :disabled="page === 1"
                :aria-label="$t('common.previousPage')"
                @click="prevPage"
                variant="text"
                width="36px"
                height="36px"
                class="mr-3"
                icon
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                :disabled="page === pageCount"
                :aria-label="$t('common.nextPage')"
                @click="nextPage"
                variant="text"
                width="36px"
                height="36px"
                icon
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:no-data>
            {{ $t('common.noDataAvailable') }}
          </template>
        </v-data-iterator>
      </v-card-text>

      <v-card-actions>
        <v-checkbox
          v-model="listExpiredLinks"
          :label="$t('routes.node.listExpiredLinks')"
          class="mt-0 pt-0 mr-2"
          hide-details
        />

        <v-select
          v-model="sortBy"
          :items="sortByItems"
          item-title="name"
          :label="$t('routes.node.sortBy')"
          item-value="id"
          variant="solo-filled"
          max-width="260px"
          hide-details
          flat
          eager
        />
        <v-spacer />
        <v-btn variant="text" @click="dialog = false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import { marked } from 'marked';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import { useTheme } from 'vuetify';
import AccesslinkUpdateDialog from './AccesslinkUpdateDialog.vue';
import AccesslinkDeleteDialog from './AccesslinkDeleteDialog.vue';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { isoToDatetime } from '@/components/formatters.js';
import { utc } from '@date-fns/utc';

export default {
  mixins: [DialogActionKeys],
  name: "AccesslinksListDialog",
  components: {
    AccesslinkUpdateDialog,
    AccesslinkDeleteDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    node: {
      type: Object,
      required: true,
    },
    pod: {
      type: Object,
      required: false,
    }
  },
  data() {
    const theme = useTheme();

    return {
      listExpiredLinks: false,
      isLoading: false,
      sortBy: null,
      itemsPerPage: 10,
      theme: theme.global.name.value
    };
  },
  mounted() {
    if (!this.isCopyToClipboardSupported){
      console.warn('Clipboard API not supported and copy to clipboard buttons were removed.');
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.refreshAccessLinks();
      }
    }
  },
  methods: {
    utc,
    isoToDatetime,
    async refreshAccessLinks() {
      this.isLoading = true;

      try {
        const accesslinkStore = useAccesslinkStore();
        await accesslinkStore.refreshAccesslinks(this.node.id);
      } finally {
        this.isLoading = false;
      }
    },
    customSort: function(items) {
      return items.sort((a, b) => (new Date(a.updatedAt) - new Date(b.updatedAt)) * -1);
    },
    selectAndCopyPasswordToClipboard: function(event, item) {
      const passwordSpan = event.target;

      const range = document.createRange();
      range.selectNodeContents(passwordSpan);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      if (this.isCopyToClipboardSupported) {
        this.copyPasswordToClipboard(item);
      }
    },
    copyPasswordToClipboard: function(item) {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(item.password)
          .then(() => {
            const localizedMessage = this.$t('routes.notifications.passwordCopiedToClipboard');
            this.$showMessage(localizedMessage);

            console.log('The password was copied to the clipboard');
          })
          .catch((error) => {
            const localizedMessage = this.$t('routes.notifications.passwordNotCopiedToClipboard');
            this.$showMessage(localizedMessage, { type: 'error' });

            console.error('Failed to copy the password to the clipboard:', error);
          });
      }
    },
    selectAndCopyURLToClipboard: function(event, item) {
      const urlSpan = event.target;

      const range = document.createRange();
      range.selectNodeContents(urlSpan);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      if (this.isCopyToClipboardSupported) {
        this.copyURLToClipboard(item);
      }
    },
    copyURLToClipboard(item) {
      if (navigator && navigator.clipboard) {
        const url = this.getItemUrl(item);
        navigator.clipboard.writeText(url)
          .then(() => {
            const localizedMessage = this.$t('routes.notifications.urlCopiedToClipboard');
            this.$showMessage(localizedMessage);

            console.log('URL copied to clipboard:', url);
          })
          .catch((error) => {
            const localizedMessage = this.$t('routes.notifications.urlNotCopiedToClipboard');
            this.$showMessage(localizedMessage, { type: 'error' });

            console.error('Failed to copy URL to clipboard:', error);
          });
      }
    },
    sortLinksByPodName(links) {
      return links.sort(function(a, b) {
        let nameA = a.pods[0]?.name?.toLowerCase() || "";
        let nameB = b.pods[0]?.name?.toLowerCase() || "";

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    },
    sortLinksByExpirationDate(links) {
      return links.sort(function(a, b) {
        const dateA = new Date(a.expireAt);
        const dateB = new Date(b.expireAt);

        if (dateA < dateB) return -1;
        if (dateB > dateB) return 1;
        return 0;
      });
    },
    enabledOptionsLabels(item) {
      const enabledOptions = [];

      // Labels should be listed in the same order as they appear in the create/edit modal
      if (item.password) enabledOptions.push(this.$t('routes.pod.menuAccesslink.passwordProtected'));
      if (item.params.showassets) enabledOptions.push(this.$t('routes.pod.menuAccesslink.showAssets'));
      if (item.params.showsubtitles) enabledOptions.push(this.$t('routes.pod.menuAccesslink.showSubtitles'));
      if (item.params.allowdownloading) enabledOptions.push(this.$t('routes.pod.menuAccesslink.allowDownloading'));
      if (item.params.allowcomments) enabledOptions.push(this.$t('routes.pod.menuAccesslink.allowComments'));
      if (item.params.allowplayingraw) enabledOptions.push(this.$t('routes.pod.menuAccesslink.allowPlayingRaw'));
      if (item.params.addversions) enabledOptions.push(this.$t('routes.pod.menuAccesslink.addVersions'));

      return enabledOptions;
    },
    isExpired(item) {
      return new Date(item.expireAt) < new Date();
    },
    orderPodsByName(items) {
      return [...items].sort((a, b) => {
        if (a.nodeid === b.nodeid) {
          return a.name.localeCompare(b.name);
        }

        return this.getNodeById(a.nodeid).name.localeCompare(this.getNodeById(b.nodeid).name);
      });
    },
    orderNodesByName(items) {
      return [...items].sort((a, b) => {
        if (a.parentid === b.parentid) {
          return a.name.localeCompare(b.name);
        }

        return this.getNodeById(a.parentid).name.localeCompare(this.getNodeById(b.parentid).name);
      });
    }
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    getAccesslinks() {
      const accesslinkStore = useAccesslinkStore();

      let self = this;
      if (this.pod) {
        return accesslinkStore.getAccesslinks.filter(function(link) {
          return !!link.pods.find(x => x.id === self.pod.id);
        });
      }

      return accesslinkStore.getAccesslinks;
    },
    filterExpiredLinks() {
      if (this.listExpiredLinks) {
        return this.getAccesslinks;
      }

      const nowDate = new Date();

      // TODO: filter the links by expiration date
      return this.getAccesslinks.filter(function(link) {
        const linkDate = new Date(link.expireAt);
        return linkDate > nowDate;
      });
    },
    getFilteredAccesslinks() {
      if (!this.sortBy) {
        return this.filterExpiredLinks;
      }

      let items = this.filterExpiredLinks.slice();

      switch (this.sortBy) {
        case 1:
        case 2: items = this.sortLinksByPodName(items); break;
        case 3:
        case 4: items = this.sortLinksByExpirationDate(items); break;
        default: break;
      }

      if (this.sortBy % 2 == 0) {
        return items.reverse();
      }

      return items;
    },
    footerOptions: function() {
      return {
        'items-per-page-options': [10, 20, 30, 50]
      };
    },
    getItemUrl: function() {
      return (item) => {
        return window.origin + item.url;
      }
    },
    getNodeById: function() {
      const nodeStore = useNodeStore();

      return (nodeId) => {
        // const nodeId = item.pod.nodeid;
        const node = nodeStore.getNodeById(nodeId);
        if (!node) {
          nodeStore.getFullNodeInfo(nodeId);
          return null;
        }

        return node;
      }
    },
    sortByItems() {
      return [
        {id: 1, name: this.$t('routes.node.fileNameAsc')},
        {id: 2, name: this.$t('routes.node.fileNameDesc')},
        {id: 3, name: this.$t('routes.node.expirationDateAsc')},
        {id: 4, name: this.$t('routes.node.expirationDateDesc')},
      ];
    },
    getExpireDateStyle() {
      const nowDate = new Date();

      return (item) => {
        const linkDate = new Date(item.expireAt);
        const styles = {};

        if (linkDate < nowDate) {
          styles.color = "red";
        }

        return styles;
      }
    },
    getNotes: function() {
      return (item) => {
        if (item && item.note) {
          return marked(item.note, { breaks: false });
        }

        return '';
      }
    },
    isCopyToClipboardSupported() {
      return navigator && navigator.clipboard;
    },
    canControlAccesslinks() {
      return  this.$canListNodeAndPod() &&
        this.$canControlNodeAndPod() &&
        this.$canListShareLinks() &&
        this.$canControlShareLinks();
    },
    getOtherFilesFoldersTranslationKey() {
      return (totalPods, totalNodes) => {
        if (totalPods > 0) {
          totalPods--;
        } else if (totalNodes > 0) {
          totalNodes--;
        }

        let key = 'routes.node.and';

        if (totalPods === 1) {
          key += 'OneFile';
        } else if (totalPods > 1) {
          key += 'ManyFiles';
        }

        if (totalNodes === 1) {
          key += 'OneFolder';
        } else if (totalNodes > 1) {
          key += 'ManyFolders';
        }

        return this.$t(key, {
          files: totalPods,
          folders: totalNodes,
        });
      }
    },
  }
}
</script>

<style lang="scss">
.vb-invited-recipients .v-chip {
  color: #1c1c1c !important;
  margin-left: 0.5rem;
}
.custom-panels {
  border-radius: 8px;
  overflow: hidden;
}

.date-info {
  font-size: 0.9rem;
}

.date-info div {
  font-size: 1.1rem;
  margin-top: 4px;
}

:deep(.v-theme--dark) .v-expansion-panel {
  background-color: #1E1E1E;
}

:deep(.v-theme--dark) .v-expansion-panel-title {
  background-color: #2D2D2D;
}

:deep(.v-theme--dark) .v-expansion-panel-text {
  background-color: #1E1E1E;
}

:deep(.v-theme--light) .v-expansion-panel {
  background-color: #FFFFFF;
}

:deep(.v-theme--light) .v-expansion-panel-title {
  background-color: #FAFAFA;
}

:deep(.v-theme--light) .v-expansion-panel-text {
  background-color: #FFFFFF;
}
</style>
